import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Typography } from 'web-library';
import { useTranslation } from 'react-i18next';

import { makeStyles } from 'tss-react/mui';
import { FileProcessingState, FileUploadState } from '../../../models/File';
import ExcelUpload from '../../../components/upload-processing-file/ExcelUpload';
import UploadingInProgress from '../../../components/upload-processing-file/UploadingInProgress';
import UploadStateAndAction from '../../../components/upload-processing-file/UploadStateAndAction';
import {
  FileProcessingLabel,
  StopProcessingButton,
} from '../../../components/upload-processing-file/FileProcessing';
import {
  FileProcessedSuccessLabel,
  ReUploadButton,
} from '../../../components/upload-processing-file/FileProcessedSuccess';
import {
  DownloadErrorFileButton,
  ErrorFileLabel,
} from '../../../components/upload-processing-file/ErrorFile';
import useUploadSectionApis from './logic/useUploadSectionApis';

const useStyles = makeStyles()((theme) =>
({
  title: {
    marginBottom: theme.spacing(2),
  },
  instruction: {
    marginBottom: theme.spacing(3),
  },
}));

interface UploadSectionProps {
  uploadAndProcessState: FileUploadState | FileProcessingState;
  setUploadAndProcessState: React.Dispatch<
    React.SetStateAction<FileUploadState | FileProcessingState>
  >;
  amendment: boolean;
}

const UploadSection = ({
  uploadAndProcessState,
  setUploadAndProcessState,
  amendment,
}: UploadSectionProps) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const { uuid: applicationUuid } = useParams<any>();
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [isReUploadModalOpen, setIsReUploadModalOpen] = useState(false);
  const [isStopProcessModalOpen, setIsStopProcessModalOpen] = useState(false);
  const [stopProcessingError, setStopProcessingError] = useState<string>();

  const {
    fileUuid,
    presignedUrlMutation,
    resetStateValueAndApiStatus,
    downloadErrorFile,
    reUploadMutation,
    stopProcessingMutation,
  } = useUploadSectionApis({
    applicationUuid,
    uploadAndProcessState,
    setUploadAndProcessState,
    uploadedFiles,
    setIsReUploadModalOpen,
    setIsStopProcessModalOpen,
    setStopProcessingError,
  });

  const conditionToShowDocumentUploader =
    uploadAndProcessState === FileUploadState.IDLE ||
    uploadAndProcessState === FileProcessingState.ERROR ||
    uploadAndProcessState === FileUploadState.FAILED;

  const uploadFileHandler = () => {
    setUploadAndProcessState(FileUploadState.UPLOADING);
    presignedUrlMutation.mutate({ uuid: applicationUuid, fileName: uploadedFiles[0].name });
  };

  const stopProcessingHandler = () => {
    setStopProcessingError(undefined);
    stopProcessingMutation.mutate({ applicationUuid, fileUuid });
  };

  const reUploadFileHandler = () => {
    reUploadMutation.mutate({ uuid: applicationUuid });
  };

  const downloadErrorFileHandler = () => {
    downloadErrorFile();
  };

  return (
    <>
      <Typography variant="h3" className={classes.title}>
        {t('specialConsideration->>UploadRankingDetail.UploadExcelTemplate')}
      </Typography>
      <Typography variant="body1" className={classes.instruction}>
        {t('specialConsideration->>UploadRankingDetail.UploadInstruction')}
      </Typography>

      {uploadAndProcessState === FileProcessingState.ERROR && (
        <UploadStateAndAction
          classes={classes}
          labels={{
            visualLabel: <ErrorFileLabel />,
            actionLabel: (
              <DownloadErrorFileButton onActionButtonClicked={downloadErrorFileHandler} />
            ),
          }}
        />
      )}

      {conditionToShowDocumentUploader && (
        <ExcelUpload
          onUploadButtonClicked={uploadFileHandler}
          uploadedFiles={uploadedFiles}
          setUploadedFiles={setUploadedFiles}
          uploadAndProcessStatus={uploadAndProcessState}
          amendment={amendment}
        />
      )}

      {uploadAndProcessState === FileUploadState.UPLOADING && (
        <UploadStateAndAction
          classes={classes}
          labels={{
            visualLabel: <UploadingInProgress />,
          }}
        />
      )}

      {uploadAndProcessState === FileProcessingState.PROCESSING && (
        <UploadStateAndAction
          classes={classes}
          labels={{
            visualLabel: <FileProcessingLabel />,
            actionLabel: (
              <StopProcessingButton
                onActionButtonClicked={stopProcessingHandler}
                onCancelButtonClicked={resetStateValueAndApiStatus}
                actionButtonApiStatus={stopProcessingMutation.status}
                isStopProcessModalOpen={isStopProcessModalOpen}
                setIsStopProcessModalOpen={setIsStopProcessModalOpen}
                apiCallError={stopProcessingError}
              />
            ),
          }}
        />
      )}

      {uploadAndProcessState === FileProcessingState.SUCCESS && (
        <UploadStateAndAction
          classes={classes}
          labels={{
            visualLabel: <FileProcessedSuccessLabel />,
            actionLabel: (
              <ReUploadButton
                onActionButtonClicked={reUploadFileHandler}
                onCancelButtonClicked={resetStateValueAndApiStatus}
                actionButtonApiStatus={reUploadMutation.status}
                isReUploadModalOpen={isReUploadModalOpen}
                setIsReUploadModalOpen={setIsReUploadModalOpen}
                amendment={amendment}
              />
            ),
          }}
        />
      )}
    </>
  );
};
export default UploadSection;
